import * as pc from 'playcanvas';

export type Ref = {
	value: number;
}

// see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/buttons#value
export const MOUSE_BUTTON = {
	LEFT: 1,
	RIGHT: 2,
	MIDDLE: 4,
} as const;
export type MOUSE_BUTTON = typeof MOUSE_BUTTON[ keyof typeof MOUSE_BUTTON ];

export const ACTION = Object.freeze( {
	NONE:                   0b0,
	ROTATE:                 0b1,
	TRUCK:                  0b10,
	SCREEN_PAN:             0b100,
	OFFSET:                 0b1000,
	DOLLY:                  0b10000,
	ZOOM:                   0b100000,
	TOUCH_ROTATE:           0b1000000,
	TOUCH_TRUCK:            0b10000000,
	TOUCH_SCREEN_PAN:       0b100000000,
	TOUCH_OFFSET:           0b1000000000,
	TOUCH_DOLLY:            0b10000000000,
	TOUCH_ZOOM:             0b100000000000,
	TOUCH_DOLLY_TRUCK:      0b1000000000000,
	TOUCH_DOLLY_SCREEN_PAN: 0b10000000000000,
	TOUCH_DOLLY_OFFSET:     0b100000000000000,
	TOUCH_DOLLY_ROTATE:     0b1000000000000000,
	TOUCH_ZOOM_TRUCK:       0b10000000000000000,
	TOUCH_ZOOM_OFFSET:      0b100000000000000000,
	TOUCH_ZOOM_SCREEN_PAN:  0b1000000000000000000,
	TOUCH_ZOOM_ROTATE:      0b10000000000000000000,
} as const );

// Bit OR of Action
export type ACTION = number;

export interface PointerInput {
	pointerId: number;
	clientX: number;
	clientY: number;
	deltaX: number;
	deltaY: number;
	mouseButton: MOUSE_BUTTON | null;
}

type mouseButtonAction = typeof ACTION.ROTATE | typeof ACTION.TRUCK | typeof ACTION.SCREEN_PAN | typeof ACTION.OFFSET  | typeof ACTION.DOLLY | typeof ACTION.ZOOM | typeof ACTION.NONE;
type mouseWheelAction  = typeof ACTION.ROTATE | typeof ACTION.TRUCK | typeof ACTION.SCREEN_PAN | typeof ACTION.OFFSET  | typeof ACTION.DOLLY | typeof ACTION.ZOOM | typeof ACTION.NONE;
type singleTouchAction =
	typeof ACTION.TOUCH_ROTATE |
	typeof ACTION.TOUCH_TRUCK |
	typeof ACTION.TOUCH_SCREEN_PAN |
	typeof ACTION.TOUCH_OFFSET |
	typeof ACTION.DOLLY |
	typeof ACTION.ZOOM |
	typeof ACTION.NONE;
type multiTouchAction =
	typeof ACTION.TOUCH_DOLLY_ROTATE |
	typeof ACTION.TOUCH_DOLLY_TRUCK |
	typeof ACTION.TOUCH_DOLLY_OFFSET |
	typeof ACTION.TOUCH_ZOOM_ROTATE |
	typeof ACTION.TOUCH_ZOOM_TRUCK |
	typeof ACTION.TOUCH_ZOOM_OFFSET |
	typeof ACTION.TOUCH_DOLLY |
	typeof ACTION.TOUCH_ZOOM |
	typeof ACTION.TOUCH_ROTATE |
	typeof ACTION.TOUCH_TRUCK |
	typeof ACTION.TOUCH_SCREEN_PAN |
	typeof ACTION.TOUCH_OFFSET |
	typeof ACTION.NONE;

export interface MouseButtons {
	left     : mouseButtonAction;
	middle   : mouseButtonAction;
	right    : mouseButtonAction;
	wheel    : mouseWheelAction;
}

export interface Touches {
	one  : singleTouchAction;
	two  : multiTouchAction;
	three: multiTouchAction;
}

export const DOLLY_DIRECTION = {
	NONE: 0,
	IN: 1,
	OUT: - 1,
} as const;
export type DOLLY_DIRECTION = typeof DOLLY_DIRECTION[ keyof typeof DOLLY_DIRECTION ];

export interface FitToOptions {
	cover: boolean;
	paddingLeft  : number;
	paddingRight : number;
	paddingBottom: number;
	paddingTop   : number;
}

export interface CameraControlsEventMap {
	update         : { type: 'update' };
	wake           : { type: 'wake' };
	rest           : { type: 'rest' };
	sleep          : { type: 'sleep' };
	transitionstart: { type: 'transitionstart' };
	controlstart   : { type: 'controlstart' };
	control        : { type: 'control' };
	controlend     : { type: 'controlend' };
}

export function isPerspectiveCamera( camera: pc.CameraComponent ): boolean {

	return camera.projection === pc.PROJECTION_PERSPECTIVE;

}

export function isOrthographicCamera( camera: pc.CameraComponent ): boolean {

	return camera.projection === pc.PROJECTION_ORTHOGRAPHIC;

}

export type CameraControlsLerpState = {
	target: [number, number, number]
} & ( {
	spherical: [number, number, number]
} | {
	position: [number, number, number]
} );